import { ISpinResponse } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import { BaseState } from '@/stateMachine/BaseState'
import { GameStatesEnum } from '@/states/GameStatesEnum'
import { applyMaxWinCapToSpinData } from '@/states/round/MaxWinCap'
import { Utilities } from '@base/utils/Utilities'
import Facade from '@/Facade'

export class GetSpinResponseState extends BaseState {
    private static readonly DEBUG_PREFIX = '[DBG:GetSpinResponseState]'
    private _spinRequestFailed: boolean = false

    public begin() {
        this._spinRequestFailed = false
        this.beginNormalSpin()
        GameSignals.casinoError.connect(this.onSpinRequestFailed)
        ;(GameSignals as any).insufficientBalance?.connect?.(this.onSpinRequestFailed)
    }

    private beginNormalSpin() {
        GameSignals.spinResponse.connect(this.end)

        const isBuyBonusSpin = !!this._model.isBuyBonusPending
        const bonusGameId = Number(Facade.bonusGameId)

        const promoCoinValue = Number((this._model.spinData as any)?.promo_details?.coin_value)
        const coinValue =
            this._model.isPromoSpinsRound() && Number.isFinite(promoCoinValue) && promoCoinValue > 0
                ? promoCoinValue
                : this._model.currentCoinBet

        GameSignals.getSpinResponse.emit({
            sessionToken: '',
            amount: this._model.fixedCoinsBet,
            coinValue,
            requestType: isBuyBonusSpin ? 6 : 2,
            pickPosition: isBuyBonusSpin ? this._model.buyBonusPickPosition : undefined,
            bonusGameId: isBuyBonusSpin ? bonusGameId : undefined,
        })
    }

    /** Spin already started; server rejected (e.g. insufficient balance) — return to idle. */
    private onSpinRequestFailed = (): void => {
        if (this._spinRequestFailed) {
            return
        }
        this._spinRequestFailed = true
        this._model.isBuyBonusPending = false
        this._model.buyBonusPickPosition = 0
        this._model.bonusBuyBet = null
        // roundStop clears panel isRoundRunning (stop → spin); unLock alone can race with gamePause.
        GameSignals.roundStop.emit()
        GameSignals.unLockGamePanel.emit()
        this._stateMachine.setState(GameStatesEnum.IDLE_STATE)
    }

    public end = (data: ISpinResponse) => {
        console.log(`${GetSpinResponseState.DEBUG_PREFIX} end(data) incoming`, {
            win_currency: data?.win_currency,
            base_win_currency: data?.base_win_currency,
            freebet: data?.freebet,
            bonus: data?.bonus,
            winning_combinations_len: Array.isArray(data?.winning_combinations)
                ? data.winning_combinations.length
                : 'n/a',
        })

        const wasBuyBonusSpin = !!this._model.isBuyBonusPending

        this._model.spinData = data
        this._model.isBuyBonusPending = false
        this._model.buyBonusPickPosition = 0
        this._model.bonusBuyBet = null

        if (applyMaxWinCapToSpinData(this._model)) {
            console.log(`${GetSpinResponseState.DEBUG_PREFIX} max win cap reached — round stops on this spin`)
        }

        // Buy bonus enters FS before StopRound; sync panel to server balance now.
        if (wasBuyBonusSpin) {
            GameSignals.balanceHasChanged.emit(this._model.balance)
        }

        const { freeround } = this._model.spinData

        !!freeround && freeround.amount_left === 0 && Utilities.isLocalStorageAvailable()
            ? localStorage.setItem(`${Facade.urlParams.playerUid}_freeround`, JSON.stringify(freeround))
            : localStorage.removeItem(`${Facade.urlParams.playerUid}_freeround`)

        console.log(`${GetSpinResponseState.DEBUG_PREFIX} model.spinData committed`, {
            win_currency: this._model.spinData?.win_currency,
            base_win_currency: this._model.spinData?.base_win_currency,
            freebet: this._model.spinData?.freebet,
            bonus: this._model.spinData?.bonus,
        })

        this._stateMachine.setState(GameStatesEnum.STOP_SPIN_STATE)
    }

    public cleanUp() {
        GameSignals.spinResponse.disconnect(this.end)
        GameSignals.casinoError.disconnect(this.onSpinRequestFailed)
        ;(GameSignals as any).insufficientBalance?.disconnect?.(this.onSpinRequestFailed)
    }
}
